import {
  Button,
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  Switch,
  useToast,
} from '@ais-ui/ui-core';
import * as z from 'zod';
import { useSettings } from '../../SettingsProvider';
import { useSaveSettings } from '@ais-ui/websockets';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import React, { useEffect, useState } from 'react';
import { useLocalization } from '@ais-ui/locale-management';
import { useSettingsPage } from '../../SettingsPageProvider';

const labelFormSchema = z.object({
  airRelatedSettings: z.boolean(),
  waterRelatedSettings: z.boolean(),
  lastOrigin: z.boolean(),
  location: z.boolean(),
  message: z.boolean(),
  metHydLatitude: z.boolean(),
  metHydLongitude: z.boolean(),
  latitude: z.boolean(),
  longitude: z.boolean(),
  metHydMmsi: z.boolean(),
  metHydTimestamp: z.boolean(),
  reportTimestamp: z.boolean(),

  siteId: z.boolean(),
  stationName: z.boolean(),
  dataTimeout: z.boolean(),
  owner: z.boolean(),
});

type LabelFormValues = z.infer<typeof labelFormSchema>;

export const MethydHoverLabelForm = () => {
  const { messages } = useLocalization();
  const { toast } = useToast();
  const { settings } = useSettings();

  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  const { mutate: mutateSettings } = useSaveSettings();

  const initialValues = {
    airRelatedSettings:
      settings['ribbonPlugin.settings']?.show_methyd_air_related || false,
    waterRelatedSettings:
      settings['ribbonPlugin.settings']?.show_methyd_water_related || false,

    lastOrigin:
      settings['ribbonPlugin.settings']?.show_methyd_last_origin || false,
    location: settings['ribbonPlugin.settings']?.show_methyd_location || false,
    message: settings['ribbonPlugin.settings']?.show_methyd_message || false,
    metHydLatitude:
      settings['ribbonPlugin.settings']?.show_methyd_latitude || false,
    metHydLongitude:
      settings['ribbonPlugin.settings']?.show_methyd_longitude || false,
    latitude:
      settings['ribbonPlugin.settings']?.show_mathyd_latitude_degree || false,
    longitude:
      settings['ribbonPlugin.settings']?.show_methyd_longitude_degree || false,
    metHydMmsi: settings['ribbonPlugin.settings']?.show_methyd_mmsi || false,
    metHydTimestamp:
      settings['ribbonPlugin.settings']?.show_methyd_timestamp || false,
    reportTimestamp:
      settings['ribbonPlugin.settings']?.show_methyd_report_timestamp || false,
    siteId: settings['ribbonPlugin.settings']?.show_methyd_site_id || false,
    stationName:
      settings['ribbonPlugin.settings']?.show_methyd_station_name || false,
    dataTimeout:
      settings['ribbonPlugin.settings']?.show_methyd_data_timeout || false,
    owner: settings['ribbonPlugin.settings']?.show_methyd_owner || false,
  };

  const form = useForm<LabelFormValues>({
    resolver: zodResolver(labelFormSchema),
    defaultValues: initialValues,
  });

  const { setUnsavedSettings } = useSettingsPage();

  useEffect(() => {
    const subscription = form.watch((formValues) => {
      const hasChanges =
        JSON.stringify(formValues) !== JSON.stringify(initialValues);
      setUnsavedSettings(hasChanges);
    });

    return () => subscription.unsubscribe();
  }, [form, form.watch, initialValues, setUnsavedSettings]);

  function onSubmit(data: LabelFormValues) {
    setIsSubmitting(true);
    const settingsToSave = {
      ...settings,
      'ribbonPlugin.settings': {
        ...settings['ribbonPlugin.settings'],
        show_methyd_air_related: data.airRelatedSettings,
        show_methyd_water_related: data.waterRelatedSettings,
        show_methyd_last_origin: data.lastOrigin,
        show_methyd_location: data.location,
        show_methyd_message: data.message,
        show_methyd_latitude: data.metHydLatitude,
        show_methyd_longitude: data.metHydLongitude,
        show_methyd_latitude_degree: data.latitude,
        show_methyd_longitude_degree: data.longitude,
        show_methyd_mmsi: data.metHydMmsi,
        show_methyd_timestamp: data.metHydTimestamp,
        // show_relative_humidity: data.airRelatedSettings,
        show_methyd_report_timestamp: data.reportTimestamp,
        show_methyd_site_id: data.siteId,
        show_methyd_station_name: data.stationName,
        show_methyd_data_timeout: data.dataTimeout,
        show_methyd_owner: data.owner,
      },
    };
    mutateSettings(settingsToSave, {
      onSuccess: () => {
        toast({
          className: 'update-methyd-hover-settings-success-toast',
          title: messages.genericToastTitleSuccess,
          description: messages.settingsMapHoverSuccessUpdateMessage,
        });
        setIsSubmitting(false);
        setUnsavedSettings(false);
      },
      onError: () => {
        toast({
          className: 'update-methyd-hover-settings-error-toast',
          title: messages.genericToastTitleError,
          description: messages.settingsMapHoverFailedUpdateMessage,
          variant: 'destructive',
        });
        setIsSubmitting(false);
      },
    });
  }

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-8">
        <div>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <FormField
                key="airRelatedSettings"
                control={form.control}
                name="airRelatedSettings"
                render={({ field }) => (
                  <FormItem className="flex flex-row items-center justify-between border rounded-lg p-4 space-x-2">
                    <div className="space-y-0.5">
                      <FormLabel className="text-base">
                        {messages.settingsMapMethydHoverAirLabel}
                      </FormLabel>
                      <FormDescription>
                        {messages.settingsMapMethydHoverAirDescription}
                      </FormDescription>
                    </div>
                    <FormControl>
                      <Switch
                        id="settings-methydLabel-airRelated"
                        checked={field.value}
                        onCheckedChange={(value) => {
                          field.onChange(value);
                        }}
                      />
                    </FormControl>
                  </FormItem>
                )}
              />
              <FormField
                key="waterRelatedSettings"
                control={form.control}
                name="waterRelatedSettings"
                render={({ field }) => (
                  <FormItem className="flex flex-row items-center justify-between border rounded-lg p-4 space-x-2">
                    <div className="space-y-0.5">
                      <FormLabel className="text-base">
                        {messages.settingsMapMethydHoverWaterLabel}
                      </FormLabel>
                      <FormDescription>
                        {messages.settingsMapMethydHoverWaterDescription}
                      </FormDescription>
                    </div>
                    <FormControl>
                      <Switch
                        id="settings-methydLabel-waterRelated"
                        checked={field.value}
                        onCheckedChange={(value) => {
                          field.onChange(value);
                        }}
                      />
                    </FormControl>
                  </FormItem>
                )}
              />
              <FormField
                key="lastOrigin"
                control={form.control}
                name="lastOrigin"
                render={({ field }) => (
                  <FormItem className="flex flex-row items-center justify-between border rounded-lg p-4 space-x-2">
                    <div className="space-y-0.5">
                      <FormLabel className="text-base">
                        {messages.settingsMapMethydHoverLastOriginLabel}
                      </FormLabel>
                      <FormDescription>
                        {messages.settingsMapMethydHoverLastOriginDescription}
                      </FormDescription>
                    </div>
                    <FormControl>
                      <Switch
                        id="settings-methydLabel-lastOrigin"
                        checked={field.value}
                        onCheckedChange={(value) => {
                          field.onChange(value);
                        }}
                      />
                    </FormControl>
                  </FormItem>
                )}
              />

              <FormField
                key="location"
                control={form.control}
                name="location"
                render={({ field }) => (
                  <FormItem className="flex flex-row items-center justify-between border rounded-lg p-4 space-x-2">
                    <div className="space-y-0.5">
                      <FormLabel className="text-base">
                        {messages.settingsMapMethydHoverLocationLabel}
                      </FormLabel>
                      <FormDescription>
                        {messages.settingsMapMethydHoverLocationDescription}
                      </FormDescription>
                    </div>
                    <FormControl>
                      <Switch
                        id="settings-methydLabel-location"
                        checked={field.value}
                        onCheckedChange={(value) => {
                          field.onChange(value);
                        }}
                      />
                    </FormControl>
                  </FormItem>
                )}
              />

              <FormField
                key="message"
                control={form.control}
                name="message"
                render={({ field }) => (
                  <FormItem className="flex flex-row items-center justify-between border rounded-lg p-4 space-x-2">
                    <div className="space-y-0.5">
                      <FormLabel className="text-base">
                        {messages.settingsMapMethydHoverMessageLabel}
                      </FormLabel>
                      <FormDescription>
                        {messages.settingsMapMethydHoverMessageDescription}
                      </FormDescription>
                    </div>
                    <FormControl>
                      <Switch
                        id="settings-methydLabel-message"
                        checked={field.value}
                        onCheckedChange={(value) => {
                          field.onChange(value);
                        }}
                      />
                    </FormControl>
                  </FormItem>
                )}
              />

              <FormField
                key="metHydLatitude"
                control={form.control}
                name="metHydLatitude"
                render={({ field }) => (
                  <FormItem className="flex flex-row items-center justify-between border rounded-lg p-4 space-x-2">
                    <div className="space-y-0.5">
                      <FormLabel className="text-base">
                        {messages.settingsMapMethydHoverLatitudeLabel}
                      </FormLabel>
                      <FormDescription>
                        {messages.settingsMapMethydHoverLatitudeDescription}
                      </FormDescription>
                    </div>
                    <FormControl>
                      <Switch
                        id="settings-methydLabel-latitude"
                        checked={field.value}
                        onCheckedChange={(value) => {
                          field.onChange(value);
                        }}
                      />
                    </FormControl>
                  </FormItem>
                )}
              />

              <FormField
                key="metHydLongitude"
                control={form.control}
                name="metHydLongitude"
                render={({ field }) => (
                  <FormItem className="flex flex-row items-center justify-between border rounded-lg p-4 space-x-2">
                    <div className="space-y-0.5">
                      <FormLabel className="text-base">
                        {messages.settingsMapMethydHoverLongitudeLabel}
                      </FormLabel>
                      <FormDescription>
                        {messages.settingsMapMethydHoverLongitudeDescription}
                      </FormDescription>
                    </div>
                    <FormControl>
                      <Switch
                        id="settings-methydLabel-longitude"
                        checked={field.value}
                        onCheckedChange={(value) => {
                          field.onChange(value);
                        }}
                      />
                    </FormControl>
                  </FormItem>
                )}
              />

              <FormField
                key="metHydMmsi"
                control={form.control}
                name="metHydMmsi"
                render={({ field }) => (
                  <FormItem className="flex flex-row items-center justify-between border rounded-lg p-4 space-x-2">
                    <div className="space-y-0.5">
                      <FormLabel className="text-base">
                        {messages.settingsMapMethydHoverMmsiLabel}
                      </FormLabel>
                      <FormDescription>
                        {messages.settingsMapMethydHoverMmsiDescription}
                      </FormDescription>
                    </div>
                    <FormControl>
                      <Switch
                        id="settings-methydLabel-metHydMmsi"
                        checked={field.value}
                        onCheckedChange={(value) => {
                          field.onChange(value);
                        }}
                      />
                    </FormControl>
                  </FormItem>
                )}
              />

              <FormField
                key="reportTimestamp"
                control={form.control}
                name="reportTimestamp"
                render={({ field }) => (
                  <FormItem className="flex flex-row items-center justify-between border rounded-lg p-4 space-x-2">
                    <div className="space-y-0.5">
                      <FormLabel className="text-base">
                        {messages.settingsMapMethydHoverTimestampLabel}
                      </FormLabel>
                      <FormDescription>
                        {messages.settingsMapMethydHoverTimestampDescription}
                      </FormDescription>
                    </div>
                    <FormControl>
                      <Switch
                        id="settings-methydLabel-metHydTimestamp"
                        checked={field.value}
                        onCheckedChange={(value) => {
                          field.onChange(value);
                        }}
                      />
                    </FormControl>
                  </FormItem>
                )}
              />
              <FormField
                key="siteId"
                control={form.control}
                name="siteId"
                render={({ field }) => (
                  <FormItem className="flex flex-row items-center justify-between border rounded-lg p-4 space-x-2">
                    <div className="space-y-0.5">
                      <FormLabel className="text-base">
                        {messages.settingsMapMethydHoverSiteIdLabel}
                      </FormLabel>
                      <FormDescription>
                        {messages.settingsMapMethydHoverSiteIdDescription}
                      </FormDescription>
                    </div>
                    <FormControl>
                      <Switch
                        id="settings-methydLabel-siteId"
                        checked={field.value}
                        onCheckedChange={(value) => {
                          field.onChange(value);
                        }}
                      />
                    </FormControl>
                  </FormItem>
                )}
              />
              <FormField
                key="stationName"
                control={form.control}
                name="stationName"
                render={({ field }) => (
                  <FormItem className="flex flex-row items-center justify-between border rounded-lg p-4 space-x-2">
                    <div className="space-y-0.5">
                      <FormLabel className="text-base">
                        {messages.settingsMapMethydHoverStationNameLabel}
                      </FormLabel>
                      <FormDescription>
                        {messages.settingsMapMethydHoverStationNameDescription}
                      </FormDescription>
                    </div>
                    <FormControl>
                      <Switch
                        id="settings-methydLabel-stationName"
                        checked={field.value}
                        onCheckedChange={(value) => {
                          field.onChange(value);
                        }}
                      />
                    </FormControl>
                  </FormItem>
                )}
              />
              <FormField
                key="dataTimeout"
                control={form.control}
                name="dataTimeout"
                render={({ field }) => (
                  <FormItem className="flex flex-row items-center justify-between border rounded-lg p-4 space-x-2">
                    <div className="space-y-0.5">
                      <FormLabel className="text-base">
                        {messages.settingsMapMethydHoverDataTimeoutLabel}
                      </FormLabel>
                      <FormDescription>
                        {messages.settingsMapMethydHoverDataTimeoutDescription}
                      </FormDescription>
                    </div>
                    <FormControl>
                      <Switch
                        id="settings-methydLabel-dataTimeout"
                        checked={field.value}
                        onCheckedChange={(value) => {
                          field.onChange(value);
                        }}
                      />
                    </FormControl>
                  </FormItem>
                )}
              />
              <FormField
                key="owner"
                control={form.control}
                name="owner"
                render={({ field }) => (
                  <FormItem className="flex flex-row items-center justify-between border rounded-lg p-4 space-x-2">
                    <div className="space-y-0.5">
                      <FormLabel className="text-base">
                        {messages.settingsMapMethydHoverOwnerLabel}
                      </FormLabel>
                      <FormDescription>
                        {messages.settingsMapMethydHoverOwnerDescription}
                      </FormDescription>
                    </div>
                    <FormControl>
                      <Switch
                        id="settings-methydLabel-owner"
                        checked={field.value}
                        onCheckedChange={(value) => {
                          field.onChange(value);
                        }}
                      />
                    </FormControl>
                  </FormItem>
                )}
              />
            </div>
          </div>
        </div>
        <div className="flex items-center justify-end gap-2 pr-2">
          <Button
            id="settings-methydLabels-save-button"
            onClick={form.handleSubmit(onSubmit)}
            type="submit"
            disabled={isSubmitting}
          >
            {isSubmitting ? messages.genericSubmitting : messages.genericSubmit}
          </Button>
        </div>
      </form>
    </Form>
  );
};
